import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

export default function UserPreferences() {
  const [level, setLevel] = useState("");
  const [showTips, setShowTips] = useState("");
  const [hero, setHero] = useState("");
  const [industry, setIndustry] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState("");
  const [isExistingUser, setIsExistingUser] = useState(false);
  const navigate = useNavigate();

  const userEmail = localStorage.getItem("userEmail");
  const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === "development" ? "http://127.0.0.1:8000" : "");

  // Fetch existing preferences on mount
  useEffect(() => {
    if (!userEmail) return;

    const fetchPreferences = async () => {
      try {
        const res = await fetch(`${API_URL}/user/preferences?email=${encodeURIComponent(userEmail)}`);
        const data = await res.json();

        if (data.preferences && Object.keys(data.preferences).length > 0) {
          const prefs = data.preferences;
          setLevel(prefs.level || "");
          setShowTips(prefs.show_tips || "");
          setHero(prefs.hero || "");
          setIndustry(prefs.industry || "");
          setIsExistingUser(true);
        }
      } catch (err) {
        console.error("Failed to fetch preferences:", err);
      } finally {
        setFetching(false);
      }
    };

    fetchPreferences();
  }, [userEmail, API_URL]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!level || !showTips) {
      setError("Please answer all required questions");
      return;
    }

    setLoading(true);

    try {
      const preferences = {
        level: level,
        show_tips: showTips,
        hero: hero || "",
        industry: industry || "",
      };

      const res = await fetch(`${API_URL}/user/preferences`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: userEmail,
          preferences: preferences,
        }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || "Failed to save preferences");
      }

      // Redirect: new users go to stockview, existing users go back
      if (isExistingUser) {
        navigate(-1);
      } else {
        navigate("/stockview");
      }
    } catch (err) {
      setError(err.message || "Failed to save preferences");
      setLoading(false);
    }
  };

  if (!userEmail) {
    // Redirect to login if no email
    navigate("/login");
    return null;
  }

  if (fetching) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900 text-gray-100">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-900 text-gray-100 py-8">
      <div className="bg-gray-800 p-8 rounded-lg shadow-lg w-full max-w-2xl relative">
        {/* Close button (only for existing users) */}
        {isExistingUser && (
          <button
            onClick={() => navigate(-1)}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-200 transition-colors"
            title="Close"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        <h2 className="text-2xl font-bold mb-6 text-center">
          {isExistingUser ? "Your Preferences" : "Welcome! Let's get to know you"}
        </h2>
        <p className="text-gray-400 mb-6 text-center text-sm">
          {isExistingUser ? "Update your preferences below" : "Help us personalize your experience"}
        </p>

        {error && (
          <div className="bg-red-900 border border-red-700 rounded p-3 text-sm text-red-100 mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Investment Level */}
          <div>
            <label className="block text-sm font-medium mb-2">
              How much do you know about investing? <span className="text-red-400">*</span>
            </label>
            <div className="space-y-2">
              {["Novice", "Intermediate", "Expert"].map((option) => (
                <label
                  key={option}
                  className="flex items-center p-3 rounded bg-gray-700 hover:bg-gray-600 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="level"
                    value={option.toLowerCase()}
                    checked={level === option.toLowerCase()}
                    onChange={(e) => setLevel(e.target.value)}
                    className="mr-3"
                  />
                  <span>{option}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Show Tips */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Would you like us to show you some helpful tips on financial data and ratios?{" "}
              <span className="text-red-400">*</span>
            </label>
            <div className="space-y-2">
              {["Yes", "No"].map((option) => (
                <label
                  key={option}
                  className="flex items-center p-3 rounded bg-gray-700 hover:bg-gray-600 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="showTips"
                    value={option.toLowerCase()}
                    checked={showTips === option.toLowerCase()}
                    onChange={(e) => setShowTips(e.target.value)}
                    className="mr-3"
                  />
                  <span>{option}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Investing Heroes */}
          <div>
            <label className="block text-sm font-medium mb-2">
              We are religious followers of Buffet and Munger, tell us who your investing heroes are?
            </label>
            <textarea
              value={hero}
              onChange={(e) => setHero(e.target.value)}
              placeholder="e.g., Warren Buffett, Charlie Munger, Peter Lynch..."
              className="w-full p-3 rounded bg-gray-700 border border-gray-600 focus:outline-none focus:border-blue-500"
              rows={3}
            />
          </div>

          {/* Industry/Edge */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Which industry do you work in? (Your edge)
            </label>
            <input
              type="text"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              placeholder="e.g., Technology, Healthcare, Finance..."
              className="w-full p-3 rounded bg-gray-700 border border-gray-600 focus:outline-none focus:border-blue-500"
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-3">
            {isExistingUser && (
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-gray-300 p-3 rounded font-medium transition-colors"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-blue-500 hover:bg-blue-600 text-white p-3 rounded font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Saving..." : isExistingUser ? "Save Changes" : "Continue to Stock View"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
